import clsx from 'clsx';
import { highlight, languages } from 'prismjs';
import React, {
  memo, ReactElement, RefObject, useCallback
} from 'react';
import { AiOutlineLoading } from 'react-icons/ai';
import { FiArrowUp, FiSquare } from 'react-icons/fi';
import Markdown from 'react-markdown';
import Editor from 'react-simple-code-editor';
import remarkGfm from 'remark-gfm';

import { Button } from '@/components/button/button';
import ChatBubble from '@/components/chatbubble/chatbubble';
import { Icon } from '@/components/icon/icon';
import { SegmentedToggle } from '@/components/SegmentedToggle/segmentedToggle';
import { ChatRoleEnum } from '@/types/chat';

import styles from './chatConversation.module.scss';

export type ChatConversationMessage =
  | { role: ChatRoleEnum; text: string }
  | { role: ChatRoleEnum; json: string };

type MessagesListProps = {
  isWaiting: boolean;
  messages: ChatConversationMessage[];
  messagesEndRef: RefObject<HTMLDivElement | null>;
};

const MessagesList = memo(
  ( { messages, isWaiting, messagesEndRef }: MessagesListProps ) => {
    const last = messages[messages.length - 1];

    return (
      <div
        className={ clsx(
          'layout__col',
          'overflow_scroll',
          styles.chat_convo_messages
        ) }
      >
        {messages.map( ( msg, i ) => (
          <div key={ i }>
            <ChatBubble type={ msg.role }>
              {'text' in msg ? (
                <Markdown remarkPlugins={ [ remarkGfm ] }>{msg.text}</Markdown>
              ) : (
                <div className={ styles.structured }>
                  <span className={ styles.lablespan }>structured content</span>
                  <Editor
                    value={ JSON.stringify( msg.json, undefined, 2 ) }
                    onValueChange={ () => null }
                    highlight={ ( code ) => highlight( code, languages.json, 'json' ) }
                    padding={ 10 }
                    readOnly
                    style={ {
                      fontFamily: '"Fira code", "Fira Mono", monospace',
                      fontSize: 12
                    } }
                  />
                </div>
              )}
              {/* show loading gif if last message is assistant and waiting for response from server */}
              {isWaiting &&
                msg.role === 'assistant' &&
                messages.length - 1 === i && (
                <Icon icon={ AiOutlineLoading } className={ styles.loadgif } />
              )}
            </ChatBubble>
          </div>
        ) )}
        {/* placeholder for loading agent message */}
        {isWaiting && last && last.role !== ChatRoleEnum.ASSISTANT && (
          <div>
            <ChatBubble type={ ChatRoleEnum.ASSISTANT }>
              <Icon icon={ AiOutlineLoading } className={ styles.loadgif } />
            </ChatBubble>
          </div>
        )}
        <div ref={ messagesEndRef } />
      </div>
    );
  }
);

MessagesList.displayName = 'MessagesList';

type OutputMode = 'text' | 'structured';

type ChatInputProps = {
  id: string;
  input: string;
  isWaiting: boolean;
  enableStructured: boolean;
  onClickChat: ( mode: string ) => void;
  onClickStop: () => void;
  onInputChange: ( value: string ) => void;
};

const ChatInput = memo(
  ( {
    id,
    input,
    isWaiting,
    enableStructured,
    onClickChat,
    onClickStop,
    onInputChange
  }: ChatInputProps ) => {
    const [ mode, setMode ] = React.useState<OutputMode>( 'text' );

    const handleKeyDown = useCallback(
      ( e: React.KeyboardEvent<HTMLInputElement> ) => {
        if ( e.key === 'Enter' && !isWaiting ) {
          onClickChat( mode );
        }
      },
      [ onClickChat, isWaiting ]
    );

    const handleInputChange = useCallback(
      ( e: React.FormEvent<HTMLInputElement> ) => {
        onInputChange( e.currentTarget.value );
      },
      [ onInputChange ]
    );

    return (
      <div className={ clsx( styles.chat_convo_input ) }>
        <input
          id={ id }
          value={ input }
          onInput={ handleInputChange }
          placeholder="Enter Message Here."
          type="text"
          onKeyDown={ handleKeyDown }
          disabled={ isWaiting }
        />
        <div style={ { float: 'left' } }>
          {enableStructured && (
            <>
              <SegmentedToggle
                value={ mode }
                onChange={ setMode }
                options={ [
                  { value: 'text', label: 'Text' },
                  { value: 'structured', label: 'Structured' }
                ] }
              />
            </>
          )}
        </div>
        {isWaiting ? (
          <Button
            variant="primary"
            className={ styles.chat_button }
            onClick={ onClickStop }
            icon={ <Icon icon={ FiSquare } size="sm" /> }
            aria-label="Stop"
          />
        ) : (
          <Button
            variant="primary"
            className={ styles.chat_button }
            onClick={ () => onClickChat( mode ) }
            icon={ <Icon icon={ FiArrowUp } size="sm" /> }
            aria-label="Send message"
            disabled={ !input.trim() }
          />
        )}
      </div>
    );
  }
);

ChatInput.displayName = 'ChatInput';

type Props = {
  id: string;
  input: string;
  isWaiting: boolean;
  enableStructured: boolean;
  messages: ChatConversationMessage[];
  messagesEndRef: RefObject<HTMLDivElement | null>;
  onClickChat: ( mode: string ) => void;
  onClickStop: () => void;
  onInputChange: ( value: string ) => void;
};

const ChatConversation = ( {
  id,
  input,
  isWaiting,
  messages,
  messagesEndRef,
  enableStructured,
  onClickChat,
  onClickStop,
  onInputChange
}: Props ): ReactElement => {
  return (
    <>
      <MessagesList
        messages={ messages }
        isWaiting={ isWaiting }
        messagesEndRef={ messagesEndRef }
      />
      <ChatInput
        id={ id }
        enableStructured={ enableStructured }
        input={ input }
        isWaiting={ isWaiting }
        onClickChat={ onClickChat }
        onClickStop={ onClickStop }
        onInputChange={ onInputChange }
      />
    </>
  );
};

ChatConversation.displayName = 'ChatConversation';

export default ChatConversation;
